import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import Logo from 'src/components/Logo';

const FOOTER_LINKS = [
  { label: 'Features', path: '/features' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Contact', path: '/contact' },
  { label: 'Blog', path: '/blog' },
];

const LEGAL_LINKS = [
  { label: 'Terms of Use', url: '/terms-of-use' },
  { label: 'Privacy Policy', url: '/privacy-policy' },
];

const SOCIAL_LINKS = [
  { label: 'Facebook', url: 'https://www.facebook.com/joinpressto' },
  { label: 'Instagram', url: 'https://www.instagram.com/joinpressto' },
  { label: 'Twitter', url: 'https://twitter.com/joinpressto' },
  { label: 'YouTube', url: 'https://www.youtube.com/@joinpressto' },
];

export default function LandingFooter() {
  const navigate = useNavigate();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1e1e1e',
        color: '#FFFFFF',
        py: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' },
          gap: { xs: 4, md: 6 },
        }}
      >
        {/* Brand column */}
        <Box>
          <Box sx={{ mb: 2, filter: 'brightness(0) invert(1)', display: 'inline-block' }}>
            <Logo variant="regular" height="28px" />
          </Box>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.875rem', color: '#999', maxWidth: 300, lineHeight: 1.7 }}>
            The writing platform where students become writers and journalists. Used by 100+ of the largest school districts in North America.
          </Typography>
        </Box>

        {/* Pages */}
        <Box>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', mb: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Product
          </Typography>
          {FOOTER_LINKS.map((link) => (
            <Typography
              key={link.label}
              onClick={() => navigate(link.path)}
              sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                color: '#999',
                mb: 1.5,
                cursor: 'pointer',
                '&:hover': { color: '#FFFFFF' },
              }}
            >
              {link.label}
            </Typography>
          ))}
        </Box>

        {/* Legal */}
        <Box>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', mb: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Legal
          </Typography>
          {LEGAL_LINKS.map((link) => (
            <MuiLink
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'block',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                color: '#999',
                mb: 1.5,
                '&:hover': { color: '#FFFFFF' },
              }}
            >
              {link.label}
            </MuiLink>
          ))}
        </Box>

        {/* Social */}
        <Box>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.8rem', mb: 2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Connect
          </Typography>
          {SOCIAL_LINKS.map((link) => (
            <MuiLink
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'block',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                color: '#999',
                mb: 1.5,
                '&:hover': { color: '#FFFFFF' },
              }}
            >
              {link.label}
            </MuiLink>
          ))}
        </Box>
      </Box>

      {/* Copyright bar */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          mt: 6,
          pt: 4,
          borderTop: '1px solid rgba(255,255,255,0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666' }}>
          &copy; {new Date().getFullYear()} Pressto
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <MuiLink href="/login" underline="none" sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', '&:hover': { color: '#FFFFFF' } }}>
            Log in
          </MuiLink>
          <MuiLink href="/register" underline="none" sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', '&:hover': { color: '#FFFFFF' } }}>
            Sign up
          </MuiLink>
        </Box>
      </Box>
    </Box>
  );
}
